/**
 * The shape of a form's submit state, and a module-scoped singleton nobody
 * should read.
 *
 * `FormSubmitState` and `FormError` are the useful exports: they type the
 * detail of `<schmancy-form>`'s `formstate` event, which is how submit state
 * reaches a consumer —
 *
 * ```ts
 * html`<schmancy-form @formstate=${(e: CustomEvent<FormSubmitState>) => …}>`
 * ```
 *
 * — scoped to the form you bound it on, which is what makes it correct when a
 * page holds more than one form.
 *
 * `formSubmitState` itself is module-scoped: EVERY form on the page writes
 * this same object, so an overlay editor's refusal lands here over the page
 * form's, and a component reading it cannot tell whose state it is holding.
 * `<schmancy-form>` keeps writing it only so consumers predating the
 * `formstate` event still see a value; it derives every write from its own
 * per-instance state and never reads this back. Do not render per-form UI
 * from it.
 *
 * It was previously served through `<schmancy-context>` from inside the
 * form's shadow root to give each form an isolated copy. That never worked:
 * the form's own writes run with the form HOST as the active host, and
 * `context-request` bubbles UP from there — straight past a provider sitting
 * BELOW it — so the writes always landed on this global while slotted
 * children read an isolated copy nothing ever wrote. The provider is gone.
 */

import { state } from '@schmancy/state'

export type FormError = {
	message: string
	code?: string
}

export type FormSubmitState = {
	status: 'idle' | 'submitting' | 'success' | 'error'
	error: FormError | null
	/**
	 * Increments on every submit attempt — a refused one included, since a
	 * refusal IS an attempt. This is what tells "never submitted" apart from
	 * "submitted and bounced"; a reset returns it to 0.
	 */
	submitCount: number
}

export const formSubmitState = state<FormSubmitState>('schmancy/form-submit').memory({
	status: 'idle',
	error: null,
	submitCount: 0,
})
